import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, CircleIcon, FileTextIcon, LockIcon, MailIcon, ReceiptIcon, ShieldAlertIcon, SparklesIcon, UsersIcon, WalletIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Meter } from '../../components/ui/Meter';
import { Tabs } from '../../components/ui/Tabs';
import { DataTable } from '../../components/ui/DataTable';
import { household } from '../../data/household';
import type { ChecklistItem } from '../../types/household';
const TABS = ['Move-in', 'People', 'Shared costs', 'Living together', 'Documents'] as const;
type Tab = (typeof TABS)[number];
const STAGES = ['forming', 'applying', 'approved', 'moving in', 'living'] as const;
const STAGE_LABEL: Record<(typeof STAGES)[number], string> = {
  forming: 'Forming',
  applying: 'Applying',
  approved: 'Approved',
  'moving in': 'Moving in',
  living: 'Living here'
};
export function Household() {
  const [tab, setTab] = useState<Tab>('Move-in');
  const [checklist, setChecklist] = useState<ChecklistItem[]>(household.checklist);
  const [tasks, setTasks] = useState(household.tasks);
  const done = checklist.filter(item => item.done).length;
  const stageIndex = STAGES.indexOf(household.stage);

  /* Who owes whom is DERIVED from the expense records, never stored — the
     same reason balances are derived everywhere else in Campuzon. */
  const balances = useMemo(() => {
    const net = new Map<string, number>();
    household.members.forEach(member => net.set(member.id, 0));
    household.expenses.filter(expense => !expense.settled).forEach(expense => {
      const share = expense.amount / expense.between.length;
      const payer = household.members.find(member => member.name === expense.paidBy);
      expense.between.forEach(memberId => {
        net.set(memberId, (net.get(memberId) ?? 0) - share);
      });
      if (payer) net.set(payer.id, (net.get(payer.id) ?? 0) + expense.amount);
    });
    return household.members.map(member => ({
      member,
      net: Math.round(net.get(member.id) ?? 0)
    }));
  }, []);
  const yourBalance = balances.find(entry => entry.member.id === 'me')?.net ?? 0;
  return <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6" data-id="element-10044" data-mp="10mdx6seuq0un">
      <PageHeader eyebrow="Housing" title={household.name} description={`${household.listingName} · ${household.area} · moving in ${household.moveIn}`} actions={<Link to={`/housing/${household.listingId}`} data-id="element-10046" data-mp="1fkemv4p4zggc">
            <Button variant="secondary" data-id="element-10047" data-mp="1fk6cbjghkn8b">View the property</Button>
          </Link>} data-id="element-10045" data-mp="15m8lco78scc0" />

      {/* The household is a real place with real people — lead with both. */}
      <div className="mt-5 overflow-hidden rounded-2xl border border-line bg-surface" data-id="element-10048" data-mp="3hd2stlt60hzt">
        <img src={household.image} alt="Three students planning together in a shared living room" className="h-48 w-full object-cover sm:h-64" data-id="element-10049" data-mp="1yh6pn9yiu2vd" />
        <div className="flex flex-wrap items-center justify-between gap-4 p-5" data-id="element-10050" data-mp="1pod630c1khbp">
          <div data-id="element-10051" data-mp="1kpig9efnpsi3">
            <p className="text-caption uppercase tracking-wide text-ink-subtle" data-id="element-10052" data-mp="1kps3wvd8bnkm">Where you are</p>
            <p className="mt-1 text-h4 font-semibold text-ink" data-id="element-10053" data-mp="2hfj0r18ztmtl">{STAGE_LABEL[household.stage]}</p>
          </div>
          <ol className="flex flex-wrap items-center gap-2" data-id="element-10054" data-mp="2ddxsxubbfyq9">
            {STAGES.map((stage, index) => <li key={stage} className="flex items-center gap-2" data-id="element-10055" data-mp="28ebczd43s4dr" data-mpi={index}>
                <span className={`inline-flex min-h-[36px] items-center rounded-full px-3 text-caption font-medium ${index < stageIndex ? 'bg-ok-50 text-ok-800' : index === stageIndex ? 'bg-brand-700 text-white' : 'bg-canvas text-ink-subtle'}`} data-id="element-10056" data-mp="23g02r19wbrv0">
                  {STAGE_LABEL[stage]}
                </span>
                {index < STAGES.length - 1 && <span aria-hidden className="h-px w-4 bg-line-strong" data-id="element-10057" data-mp="26xnmv1ryw3mn" />}
              </li>)}
          </ol>
        </div>
      </div>

      <div className="mt-6" data-id="element-10058" data-mp="2btl02z7xotv7">
        <Tabs tabs={TABS} value={tab} onChange={setTab} label="Household sections" data-id="element-10059" data-mp="2zl05j0v0brao" />
      </div>

      {tab === 'Move-in' && <div className="mt-5 space-y-4" data-id="element-10060" data-mp="33lttfu8g963k">
          <Panel title="Move-in checklist" description="Ticking a step records that your household did it. It does not notify the property manager or change anything on the tenancy." data-id="element-10061" data-mp="2pncdiioscxw7">
            <Meter value={Math.round(done / checklist.length * 100)} label={`${done} of ${checklist.length} steps done`} data-id="element-10062" data-mp="1e7nqjrbj7yve" />
            <ul className="mt-4 divide-y divide-line" data-id="element-10063" data-mp="1j575yujyuhox">
              {checklist.map(item => <li key={item.id} className="flex items-start gap-3 py-3" data-id="element-10064" data-mp="1bpvcle2qqufj">
                  <button type="button" onClick={() => setChecklist(current => current.map(entry => entry.id === item.id ? {
              ...entry,
              done: !entry.done
            } : entry))} aria-pressed={item.done} aria-label={`${item.done ? 'Mark as not done' : 'Mark as done'}: ${item.label}`} className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full hover:bg-canvas" data-id="element-10065" data-mp="16o615xblweqx">
                    {item.done ? <CheckIcon aria-hidden className="h-5 w-5 text-ok-700" data-id="element-10066" data-mp="988oavlr0byu" /> : <CircleIcon aria-hidden className="h-5 w-5 text-ink-subtle" data-id="element-10067" data-mp="495r1xc0fa1f" />}
                  </button>
                  <span className="min-w-0 flex-1" data-id="element-10068" data-mp="24acf7rghc0bb">
                    <span className={`block text-body font-medium ${item.done ? 'text-ink-muted line-through' : 'text-ink'}`} data-id="element-10069" data-mp="29arp291jypoj">
                      {item.label}
                    </span>
                    <span className="block text-body-sm leading-snug text-ink-muted" data-id="element-10070" data-mp="1ldufamrh8u3z">
                      {item.detail}
                    </span>
                    {item.blockedBy && <span className="mt-1 flex items-start gap-1.5 text-caption text-warn-800" data-id="element-10071" data-mp="1vbs53eoziyyp">
                        <ShieldAlertIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-10072" data-mp="1zddir29cfh9y" />
                        {item.blockedBy}
                      </span>}
                  </span>
                  <Badge plain className="shrink-0" data-id="element-10073" data-mp="3as90wy7780v9">
                    {item.owner}
                  </Badge>
                </li>)}
            </ul>
          </Panel>

          <Panel title="What Campuzon does and does not do here" data-id="element-10074" data-mp="z705x8he7cb8">
            <ul className="space-y-2 text-body-sm text-ink-muted" data-id="element-10075" data-mp="v43qfqn5fwbi">
              <li data-id="element-10076" data-mp="qc25lgbx0x9v">Rent is paid to the property manager. Campuzon records it; it never holds it.</li>
              <li data-id="element-10077" data-mp="lek6sirm3yfl">
                A maintenance request raised here reaches the property console — this household
                cannot resolve it on its own.
              </li>
              <li data-id="element-10078" data-mp="bfumkjc8u1w6">
                Leaving a household keeps every shared cost record, so a settled balance stays
                provable.
              </li>
            </ul>
          </Panel>
        </div>}

      {tab === 'People' && <div className="mt-5 space-y-4" data-id="element-10079" data-mp="2ghbjm8h159c">
          <Panel title="Who lives here" description="A household is people who agreed to live together. It is not a group chat and it grants nobody access to your academic, financial or private records." bodyClassName="p-0" data-id="element-10080" data-mp="21s0jpylya9hy">
            <ul className="divide-y divide-line" data-id="element-10081" data-mp="15od6bqtzl85u">
              {household.members.map(member => <li key={member.id} className="flex flex-wrap items-center gap-3 p-4" data-id="element-10082" data-mp="1aor5skhiv9s9">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-50 text-body font-semibold text-brand-700" data-id="element-10083" data-mp="vxiapvuuow7q">
                    {member.initials}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-10084" data-mp="mzgerupz4trb">
                    <span className="block text-body font-medium text-ink" data-id="element-10085" data-mp="qzqi0zmdt62h">{member.name}</span>
                    <span className="block text-caption text-ink-muted" data-id="element-10086" data-mp="czxwrjtmji1w">
                      {member.handle} · {member.programme}
                    </span>
                    <span className="block text-caption text-ink-subtle" data-id="element-10087" data-mp="27cxiluoll8hm">{member.joined}</span>
                  </span>
                  <span className="flex flex-wrap items-center gap-2" data-id="element-10088" data-mp="2ccjvu44feoqf">
                    {member.compatibility !== undefined && <Badge tone="brand" icon={<SparklesIcon className="h-3.5 w-3.5" data-id="element-10090" data-mp="mkfn54137ap" />} data-id="element-10089" data-mp="pgdyujsfxzo">
                        {member.compatibility}% compatible
                      </Badge>}
                    <Badge tone={member.role === 'Invited' ? 'warn' : 'neutral'} data-id="element-10091" data-mp="af9l9jcwdqy4">
                      {member.role}
                    </Badge>
                    <span className="cz-num text-body-sm font-medium text-ink" data-id="element-10092" data-mp="jf64h5gipxls">
                      {member.sharePerYear > 0 ? `₦${member.sharePerYear.toLocaleString()}/yr` : 'No share yet'}
                    </span>
                  </span>
                </li>)}
            </ul>
          </Panel>

          <Panel title="Add someone" data-id="element-10093" data-mp="oesmrtb7e41g">
            <p className="text-body-sm text-ink-muted" data-id="element-10094" data-mp="2v4p7e1168rhf">
              An invitation goes to their Campuzon account. They see the property, the rent, the
              proposed share and the house rules before they accept — nobody is added to a
              household without agreeing to it.
            </p>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-10095" data-mp="2tob2hqt6svhy">
              <Button variant="primary" icon={<MailIcon className="h-4 w-4" data-id="element-10097" data-mp="2ooyjxi9cmal6" />} data-id="element-10096" data-mp="2tmq9w6qfnmmq">
                Invite someone
              </Button>
              <Link to="/housing/roommates" data-id="element-10098" data-mp="2jq3ongu72tk6">
                <Button variant="secondary" icon={<UsersIcon className="h-4 w-4" data-id="element-10100" data-mp="2js82aqhwb6xn" />} data-id="element-10099" data-mp="2jr5a8f8u6yg5">
                  Find a compatible roommate
                </Button>
              </Link>
            </div>
          </Panel>
        </div>}

      {tab === 'Shared costs' && <div className="mt-5 space-y-4" data-id="element-10101" data-mp="37napku11x2c0">
          <Panel title="Where you stand" data-id="element-10102" data-mp="37lzox32wkap4">
            <p className="text-body-lg text-ink" data-id="element-10103" data-mp="32ozgwmmhsxvd">
              {yourBalance === 0 ? 'You are square with the household.' : yourBalance > 0 ? `The household owes you ₦${Math.abs(yourBalance).toLocaleString()}.` : `You owe the household ₦${Math.abs(yourBalance).toLocaleString()}.`}
            </p>
            <p className="mt-1 text-caption text-ink-muted" data-id="element-10104" data-mp="7iq2ebsjtz7f">
              Derived from unsettled expenses below, not a stored balance. Settling is a payment
              between people — Campuzon records it and holds nothing.
            </p>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-10105" data-mp="lgxw8auagked">
              {balances.filter(entry => entry.member.sharePerYear > 0).map(entry => <li key={entry.member.id} className="flex items-center justify-between gap-3 rounded-xl border border-line px-3 py-2" data-id="element-10106" data-mp="ugup6onpo5f8">
                    <span className="text-body-sm text-ink" data-id="element-10107" data-mp="146mk5jzq30k8">{entry.member.name}</span>
                    <span className={`cz-num text-body-sm font-medium ${entry.net < 0 ? 'text-danger-700' : 'text-ok-800'}`} data-id="element-10108" data-mp="195z7s6yf4bnv">
                      {entry.net < 0 ? '−' : '+'}₦{Math.abs(entry.net).toLocaleString()}
                    </span>
                  </li>)}
            </ul>
            <div className="mt-4 flex flex-wrap gap-2" data-id="element-10109" data-mp="jfqv85gpm1vv">
              <Link to="/money" data-id="element-10110" data-mp="y7swntyfh2y0">
                <Button variant="primary" icon={<WalletIcon className="h-4 w-4" data-id="element-10112" data-mp="tgawh848b6e3" />} data-id="element-10111" data-mp="y8uag66tw3jf">
                  Settle in Money
                </Button>
              </Link>
              <Button variant="secondary" icon={<ReceiptIcon className="h-4 w-4" data-id="element-10114" data-mp="179ipn47yk9xw" />} data-id="element-10113" data-mp="176efzrbq4lzu">
                Add an expense
              </Button>
            </div>
          </Panel>

          <Panel title="Shared expenses" bodyClassName="p-0" data-id="element-10115" data-mp="1h65a3rwdgyze">
            <DataTable caption="Expenses shared by this household" rowKey={expense => expense.id} rows={household.expenses} columns={[{
          header: 'Expense',
          primary: true,
          cell: expense => expense.label
        }, {
          header: 'Category',
          cell: expense => expense.category
        }, {
          header: 'Paid by',
          cell: expense => expense.paidBy
        }, {
          header: 'Date',
          cell: expense => expense.when
        }, {
          header: 'Split between',
          cell: expense => `${expense.between.length} people`
        }, {
          header: 'Amount',
          align: 'right',
          cell: expense => <span className="cz-num" data-id="element-10117" data-mp="1ao23xatmqy33">₦{expense.amount.toLocaleString()}</span>
        }, {
          header: 'Status',
          status: true,
          cell: expense => <Badge tone={expense.settled ? 'ok' : 'warn'} data-id="element-10118" data-mp="15pqwf6qt4zo1">
                      {expense.settled ? 'Settled' : 'Outstanding'}
                    </Badge>
        }]} data-id="element-10116" data-mp="1h3j07lm0e7e0" />
          </Panel>
        </div>}

      {tab === 'Living together' && <div className="mt-5 grid gap-4 lg:grid-cols-2" data-id="element-10119" data-mp="102aej85jr5sa">
          <Panel title="Tasks" description="Who does what, and when. Nobody is scored on this — it exists so the conversation happens once rather than every week." data-id="element-10120" data-mp="zzo7d9mkisal">
            <ul className="divide-y divide-line" data-id="element-10121" data-mp="r36v232gwod9">
              {tasks.map(task => <li key={task.id} className="flex items-center gap-3 py-3" data-id="element-10122" data-mp="m4dd9yg5eb7a">
                  <button type="button" onClick={() => setTasks(current => current.map(entry => entry.id === task.id ? {
              ...entry,
              done: !entry.done
            } : entry))} aria-pressed={task.done} aria-label={`${task.done ? 'Mark as not done' : 'Mark as done'}: ${task.label}`} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full hover:bg-canvas" data-id="element-10123" data-mp="h4qz9mshwqxf">
                    {task.done ? <CheckIcon aria-hidden className="h-5 w-5 text-ok-700" data-id="element-10124" data-mp="29z8saw0n8rd3" /> : <CircleIcon aria-hidden className="h-5 w-5 text-ink-subtle" data-id="element-10125" data-mp="2eyv5j5gh27lw" />}
                  </button>
                  <span className="min-w-0 flex-1" data-id="element-10126" data-mp="2j06ois4ubul4">
                    <span className={`block text-body ${task.done ? 'text-ink-muted line-through' : 'text-ink'}`} data-id="element-10127" data-mp="1mazas6agqprr">
                      {task.label}
                    </span>
                    <span className="block text-caption text-ink-muted" data-id="element-10128" data-mp="1v9az21nuxbh1">
                      {task.assignee} · {task.cadence} · {task.due}
                    </span>
                  </span>
                </li>)}
            </ul>
          </Panel>

          <Panel title="House rules" description="A rule only counts once everyone on the agreement has accepted it. One that hasn’t says so." data-id="element-10129" data-mp="17k8i6w11rmnl">
            <ul className="space-y-3" data-id="element-10130" data-mp="25y5oknlqyrtt">
              {household.rules.map(rule => {
            const named = household.members.filter(member => member.sharePerYear > 0);
            const outstanding = named.length - rule.agreedBy.length;
            return <li key={rule.id} className="rounded-xl border border-line p-3" data-id="element-10131" data-mp="1rxcq5rsbrclf">
                    <p className="text-body font-medium text-ink" data-id="element-10132" data-mp="1myhzy4s12lra">{rule.rule}</p>
                    <p className="mt-1 text-caption text-ink-muted" data-id="element-10133" data-mp="1n0merhrgt56c">
                      {outstanding === 0 ? 'Agreed by everyone on the tenancy.' : `${outstanding} ${outstanding === 1 ? 'person has' : 'people have'} not agreed yet.`}
                    </p>
                    {rule.note && <p className="mt-1 text-caption text-ink-subtle" data-id="element-10134" data-mp="38cmfl3jyj23p">{rule.note}</p>}
                  </li>;
          })}
            </ul>
          </Panel>
        </div>}

      {tab === 'Documents' && <div className="mt-5 space-y-4" data-id="element-10135" data-mp="3ksdp1jcumayr">
          <Panel title="Documents" bodyClassName="p-0" data-id="element-10136" data-mp="35xpmoe2qvsnd">
            <ul className="divide-y divide-line" data-id="element-10137" data-mp="35zajybgg1pox">
              {household.documents.map(document => <li key={document.id} className="flex flex-wrap items-center gap-3 p-4" data-id="element-10138" data-mp="3ay3x22rtjeok">
                  <FileTextIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-muted" data-id="element-10139" data-mp="2ik83fy6kifb" />
                  <span className="min-w-0 flex-1" data-id="element-10140" data-mp="7esgmahvhddi">
                    <span className="block text-body font-medium text-ink" data-id="element-10141" data-mp="3owle5hn7xsxi">{document.name}</span>
                    <span className="block text-caption text-ink-muted" data-id="element-10142" data-mp="3tumx82kn9s8z">
                      {document.kind} · added by {document.addedBy} on {document.added}
                    </span>
                  </span>
                  <Badge tone={document.visibleTo === 'Household' ? 'neutral' : 'brand'} icon={document.visibleTo === 'Only me' ? <LockIcon className="h-3.5 w-3.5" data-id="element-10144" data-mp="2hiq44faxmwsr" /> : undefined} data-id="element-10143" data-mp="q6bnisf0pbkt">
                    {document.visibleTo === 'Household' ? 'Whole household' : 'Only me'}
                  </Badge>
                </li>)}
            </ul>
          </Panel>
          <Panel title="Who can see what" data-id="element-10145" data-mp="3582f9kptlexp">
            <p className="text-body-sm text-ink-muted" data-id="element-10146" data-mp="356r9xggq7z4h">
              A document you upload stays yours. Sharing it with the household is a choice you make
              per file, and you can withdraw it. Your identification is never shared by default, and
              the property manager sees only what your application included.
            </p>
          </Panel>
        </div>}
    </div>;
}